import { TOptions } from 'i18next'

import i18n, { NSKey } from '@src/i18n'

/**
 * Namespace-bound translator backed by the i18n singleton — NOT the `useTranslation` hook.
 *
 * ⚠️ USE THIS ONLY FOR TRANSLATIONS PRODUCED INSIDE A HOOK THAT DECLARES DEPENDENCIES
 * (the message in a `useEffect`, `useCallback` or `useMemo`). It exists for one reason
 * and should not be used anywhere else.
 *
 * Why it exists:
 * The `t` returned by `useTranslation()` is tied to the render cycle and is not a stable
 * reference — it changes identity on i18n events (language change, late namespace load)
 * and, in some setups, between ordinary re-renders. Listing it in a dependency array makes
 * the hook re-run whenever `t` changes — at best a stray re-run, at worst an infinite
 * render/dispatch loop. Because `tStable` reads from the i18n singleton, its
 * reference never changes, so it never needs to appear in a dependency array.
 *
 * ❌ DO NOT use it for text rendered into JSX. It is NOT reactive: the returned string is
 * resolved at call time and will NOT update when the user switches language. For anything
 * shown in the UI, use the `t` from `useTranslation()`.
 *
 * @example
 * useEffect(() => {
 *     dispatch(fetchSomethingAsync()).catch(() => {
 *         toast.error(tStable(NSKey.Common, 'errors.somethingGoesWrong'))
 *     })
 * }, [dispatch]) // no `t` in the deps → no surprise re-runs
 */
export const tStable = (ns: NSKey, message: string, options?: TOptions): string => i18n.t(message, { ns, ...options })
